import { Link } from 'react-router-dom'
import { ArrowLeft, FileText } from 'lucide-react'
import { useTranslation } from '../i18n/LanguageContext'

export function TermsOfService() {
  const { language } = useTranslation()

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Back Button */}
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-purple-500 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">KeyCore AI</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          {language === 'en' ? (
            // English Version
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
              <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By accessing and using KeyCore AI Studio ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  KeyCore AI Studio is an AI-powered multimodal content creation platform that provides:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>AI image generation from text prompts</li>
                  <li>Text-to-speech audio synthesis</li>
                  <li>Content management and storage</li>
                  <li>User account and quota management</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Account Creation</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To use our service, you must create an account with accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Account Usage</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>You must be at least 13 years old to use our service</li>
                  <li>You are responsible for all activities under your account</li>
                  <li>You must notify us immediately of any unauthorized use</li>
                  <li>We reserve the right to suspend or terminate accounts at our discretion</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Usage Guidelines</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Acceptable Use</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You agree to use our service only for lawful purposes and in compliance with applicable laws and regulations.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Prohibited Activities</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Generate content that violates laws or regulations</li>
                  <li>Create harmful, offensive, or inappropriate content</li>
                  <li>Attempt to reverse engineer or misuse our AI systems</li>
                  <li>Share account credentials or API keys</li>
                  <li>Exceed your allocated usage quotas</li>
                  <li>Interfere with or disrupt our service infrastructure</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Content Ownership and Rights</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 User Content</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You retain ownership of the content you create using our platform. However, by using our service, you grant us a limited license to process and store your content for service provision.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 AI Generated Content</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Content generated by our AI systems is provided to you under a royalty-free license for personal and commercial use, subject to our content guidelines.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Usage Quotas and Billing</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Free tier users receive limited monthly quotas</li>
                  <li>Paid users receive higher quotas based on their subscription</li>
                  <li>Quotas reset monthly on the first day of each month</li>
                  <li>Excess usage may result in service throttling or additional charges</li>
                  <li>We reserve the right to modify quotas with advance notice</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. API Usage</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you use our API programmatically:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>You must comply with rate limiting guidelines</li>
                  <li>You are responsible for securing your API keys</li>
                  <li>Misuse may result in immediate suspension</li>
                  <li>You must not use our API for competitive services</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Service Availability</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>We strive for high availability but do not guarantee uptime</li>
                  <li>Service may be interrupted for maintenance or technical issues</li>
                  <li>We are not liable for service interruptions or data loss</li>
                  <li>Emergency maintenance will be communicated in advance when possible</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Termination</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may terminate or suspend your account and access to our service immediately, without prior notice, for any reason, including breach of these terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers and Limitations</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">10.1 AI Content Disclaimer</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AI-generated content may contain inaccuracies, biases, or inappropriate elements. You are responsible for reviewing and validating all generated content before use.
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">10.2 Limitation of Liability</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our service is provided "as is" without warranties. We shall not be liable for any indirect, incidental, or consequential damages.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to modify these terms at any time. Continued use of our service after changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For questions about these Terms of Service, please contact us at:
                </p>
                <p className="text-gray-700">
                  Email: legal@keycore.tech<br />
                  Address: KeyCore AI Studio Legal Team
                </p>
              </section>
            </>
          ) : (
            // Chinese Version
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">使用条款</h1>
              <p className="text-gray-600 mb-6">最后更新: {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. 条款接受</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  通过访问和使用 KeyCore AI Studio（"服务"），您接受并同意受本协议条款和规定的约束。如果您不同意遵守上述条款，请不要使用本服务。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. 服务描述</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  KeyCore AI Studio 是一个AI驱动的多模态内容创作平台，提供：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>基于文本提示的AI图像生成</li>
                  <li>文本转语音音频合成</li>
                  <li>内容管理和存储</li>
                  <li>用户账户和配额管理</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. 用户账户</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 账户创建</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  要使用我们的服务，您必须使用准确和完整的信息创建账户。您负责维护账户凭据的机密性。
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 账户使用</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>您必须至少13岁才能使用我们的服务</li>
                  <li>您对账户下的所有活动负责</li>
                  <li>任何未经授权的使用必须立即通知我们</li>
                  <li>我们保留暂停或终止账户的权利</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. 使用指南</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 可接受使用</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  您同意仅将我们的服务用于合法目的，并遵守适用的法律法规。
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 禁止活动</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>生成违反法律法规的内容</li>
                  <li>创建有害、冒犯性或不适当的内容</li>
                  <li>尝试逆向工程或滥用我们的AI系统</li>
                  <li>分享账户凭据或API密钥</li>
                  <li>超出分配的使用配额</li>
                  <li>干扰或破坏我们的服务基础设施</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. 内容所有权和权利</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 用户内容</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  您保留使用我们平台创建的内容的所有权。但是，通过使用我们的服务，您授予我们有限许可来处理和存储您的内容以提供服务。
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 AI生成内容</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们AI系统生成的内容在免版税许可下提供给您，用于个人和商业用途，但须遵守我们的内容指南。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. 使用配额和计费</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>免费用户获得有限的每月配额</li>
                  <li>付费用户根据订阅获得更高的配额</li>
                  <li>配额每月1号重置</li>
                  <li>超额使用可能导致服务限制或额外费用</li>
                  <li>我们保留修改配额的权利，并提前通知</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. API使用</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  如果您以编程方式使用我们的API：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>您必须遵守速率限制指南</li>
                  <li>您负责保护您的API密钥安全</li>
                  <li>滥用可能导致立即暂停</li>
                  <li>您不得将我们的API用于竞争性服务</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. 服务可用性</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>我们努力提供高可用性，但不保证正常运行时间</li>
                  <li>服务可能因维护或技术问题而中断</li>
                  <li>我们不对服务中断或数据丢失负责</li>
                  <li>紧急维护将在可能的情况下提前通知</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. 终止</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们可能因任何原因立即终止或暂停您的账户和对我们服务的访问，包括违反这些条款，而无需事先通知。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. 免责声明和责任限制</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">10.1 AI内容免责声明</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AI生成的内容可能包含不准确、偏见或不适当的元素。您负责在使用前审查和验证所有生成的内容。
                </p>

                <h3 className="text-xl font-medium text-gray-900 mb-3">10.2 责任限制</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们的服务按"原样"提供，不提供任何保证。我们不对任何间接、偶然或后果性损害负责。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. 适用法律</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  本条款应受适用法律管辖并据其解释。任何争议应通过具有约束力的仲裁解决。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. 条款变更</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们保留随时修改这些条款的权利。变更后继续使用我们的服务即构成对新条款的接受。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. 联系信息</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  如对本使用条款有任何疑问，请通过以下方式联系我们：
                </p>
                <p className="text-gray-700">
                  邮箱: legal@keycore.tech<br />
                  地址: KeyCore AI Studio 法律团队
                </p>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
